/**
 * CustomerCard — the booking detail view's customer panel (BON-145): avatar,
 * provenance and prior-booking history. Pure (data in → markup out).
 *
 * Split out of the former `BookingDetailCards.tsx` (BON-335 round 4), which
 * held three components and tripped `react/no-multi-comp` the moment it was
 * edited. One component per file, per the coding standards.
 */
import type { BookingDetail } from '../../api/bookingsV2';

import { cardCls, fmtCreatedDate, initials, META, Pill } from './bookingCardShared';
import { historyLabel } from './bookingDetailModel';

export function CustomerCard({ booking }: { booking: BookingDetail }) {
  const { customer, source } = booking;
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
  const history = historyLabel(customer.priorBookingsCount);
  return (
    <div className={cardCls}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-bark-900 dark:text-stone-100">Customer</h2>
          <span className={META}>{source === 'nebari' ? 'Online' : 'Migrated'}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 w-11 h-11 rounded-full bg-moss-600 text-white flex items-center justify-center text-sm font-semibold">
            {initials(customer.firstName, customer.lastName, customer.email)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-bark-900 dark:text-stone-100 truncate">
              {name || customer.email || 'Unknown'}
            </p>
            {customer.email && (
              <p className="text-xs text-stone-500 dark:text-stone-400 truncate">
                {customer.email}
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-700 flex flex-wrap items-center gap-2">
          <Pill>{history.count}</Pill>
          {customer.priorBookingsCount > 0 && <Pill>Returning</Pill>}
          <span className={`ml-auto ${META}`}>Created {fmtCreatedDate(booking.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}
